import { ApiResponseError } from '@/api/ApiResponseError';
import { get } from '@/api/fetch';
import { captureError } from '@smg-real-estate/browser-rum';

export interface ImportPropertyValidationError {
  path: string;
  keyword: string;
  message: string;
}

export interface ImportPropertyError {
  code: string;
  message: string;
  validationErrors?: ImportPropertyValidationError[];
}

export interface ImportPropertyWarning {
  code: string;
  message: string;
  url?: string;
}

export interface ImportPropertyFeedback {
  title: Record<string, string>;
  referenceId: string;
  listingId?: string | null;
  status: string;
  errors?: ImportPropertyError[] | null;
  warnings?: ImportPropertyWarning[] | null;
}

export interface ImportStatistics {
  deletedCount?: number;
  failedCount?: number;
  ignoredCount?: number;
  insertedCount?: number;
  processingCount?: number;
  succeededCount?: number;
  succeededWithWarnCount?: number;
  unchangedCount?: number;
  updatedCount?: number;
  [key: string]: unknown;
}

export interface LatestImportMeta {
  totalCount?: number;
  importReferenceId: string;
  status: string;
  createdAt: string;
  importStatistics?: ImportStatistics | null;
  metaSoftwareName?: string | null;
  propertyCount?: number;
}

export interface LatestImportResponse {
  data?: ImportPropertyFeedback[] | null;
  meta: LatestImportMeta;
}

export class ImportReportApiError extends Error {
  endpoint: string;

  constructor(endpoint: string, message: string) {
    super(`Import Report API error: ${message}`);
    this.name = 'ImportReportApiError';
    this.endpoint = endpoint;
  }
}

/**
 * Fetches the latest SwissRETS import for the authenticated agency.
 * Agency ID is extracted from JWT on the backend.
 * @param includeProperties - Whether to include property-level feedback
 * @param limit - Maximum number of properties to return
 * @param offset - Number of properties to skip
 * @returns The latest import data or null if no imports exist (404)
 */
export async function getLatestImport(
  includeProperties = true,
  limit?: number,
  offset?: number,
): Promise<LatestImportResponse | null> {
  const params = new URLSearchParams();
  if (includeProperties) {
    params.append('includeProperties', 'true');
  }
  if (limit !== undefined) {
    params.append('limit', String(limit));
  }
  if (offset !== undefined) {
    params.append('offset', String(offset));
  }
  const queryString = params.toString();
  const baseUrl = import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL;
  const url = queryString
    ? `${baseUrl}/business/imports/latest?${queryString}`
    : `${baseUrl}/business/imports/latest`;

  try {
    const response = await get(url);
    return response.json();
  } catch (error) {
    // 404 means no imports exist - return null
    if (error instanceof ApiResponseError && error.statusCode === 404) {
      return null;
    }

    captureError(new ImportReportApiError(url, (error as Error).message));
    throw error;
  }
}
